import { ServiceConfig } from '../services/config'
import { createAsyncThunk } from '@reduxjs/toolkit'
import {
  AssigneeOption,
  GetSalesTeamInput,
  GetSalesTeamListInput,
  ISalesTeam,
  ISalesTeamPosition,
  IUpdateDefaultSalesTeamsInput,
  IUserPosition,
  SalesTeamCreateModel,
  SalesTeamPositionCreateModel,
  SalesTeamPositionDeleteModel,
  SalesTeamPositionUpdateModel,
  SalesTeamUpdateModel,
} from './typings'
import { APIValidationError, get, post, put, remove } from '../services/crud'
import { objectToQueryString } from '@sales-center/common'
import { loadFilterPreferences } from '../userPreferences'

const baseUrl = `${ServiceConfig.baseAPIUrl}/SalesTeam`

export const getSalesTeamList = createAsyncThunk<
  ISalesTeam[],
  GetSalesTeamListInput
>('salesTeams/all', async (model, thunkApi) => {
  const response = await get(`${baseUrl}?${objectToQueryString(model || '')}`)
  if (response.ok) return await response.json()
  return thunkApi.rejectWithValue(await response.json())
})

export const getCurrentUserSalesTeams = createAsyncThunk<ISalesTeam[], void>(
  'salesTeams/allCurrentUser',
  async (_, thunkApi) => {
    const response = await get(`${baseUrl}/getAllForCurrentUser`)
    if (response.ok) return await response.json()
    return thunkApi.rejectWithValue(await response.json())
  },
)

export const createSalesTeam = createAsyncThunk<
  ISalesTeam,
  SalesTeamCreateModel,
  { rejectValue: APIValidationError }
>('salesTeam/create', async (team, thunkApi) => {
  const response = await post(`${baseUrl}`, team)

  if (response.ok) return await response.json()
  return thunkApi.rejectWithValue(await response.json())
})

export const getSalesTeam = createAsyncThunk<
  ISalesTeam,
  GetSalesTeamInput,
  { rejectValue: string }
>('salesTeams/get', async (model, thunkApi) => {
  const response = await get(
    `${baseUrl}/${
      model.id
    }?includeUsers=${!!model.includeUsers}&includeInactiveUsers=${!!model.includeUsers}`,
  )
  if (response.ok) return await response.json()
  return thunkApi.rejectWithValue(await response.json())
})

export const updateSalesTeam = createAsyncThunk<
  ISalesTeam,
  SalesTeamUpdateModel,
  { rejectValue: APIValidationError }
>('salesTeam/update', async (team, thunkApi) => {
  const response = await put(`${baseUrl}/update`, team)

  if (response.ok) return await response.json()
  return thunkApi.rejectWithValue(await response.json())
})

export const deleteSalesTeam = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>('salesTeam/delete', async (id, thunkApi) => {
  const response = await remove(`${baseUrl}/${id}`)
  if (response.ok) {
    thunkApi.dispatch(loadFilterPreferences())
    return id
  }
  return thunkApi.rejectWithValue(await response.json())
})

export const createSalesTeamPosition = createAsyncThunk<
  ISalesTeamPosition,
  SalesTeamPositionCreateModel,
  { rejectValue: APIValidationError }
>('salesTeam/position/create', async (position, thunkApi) => {
  const response = await post(
    `${baseUrl}/${position.salesTeamId}/position`,
    position,
  )

  if (response.ok) return await response.json()
  return thunkApi.rejectWithValue(await response.json())
})

export const deleteSalesTeamPosition = createAsyncThunk<
  number,
  SalesTeamPositionDeleteModel,
  { rejectValue: string }
>('salesTeamPosition/delete', async (model, thunkApi) => {
  const response = await remove(
    `${baseUrl}/${model.id}/position/${model.positionId}`,
  )
  if (response.ok) {
    thunkApi.dispatch(getSalesTeam({ id: model.id, includeUsers: true }))
    return model.positionId
  }
  return thunkApi.rejectWithValue(await response.json())
})

export const updateSalesTeamPosition = createAsyncThunk<
  ISalesTeamPosition[],
  SalesTeamPositionUpdateModel,
  { rejectValue: APIValidationError }
>('salesTeam/position/update', async (position, thunkApi) => {
  const response = await put(
    `${baseUrl}/${position.salesTeamId}/position`,
    position,
  )

  if (response.ok) {
    const result = await response.json()
    thunkApi.dispatch(
      getSalesTeam({ id: position.salesTeamId, includeUsers: true }),
    )
    return result
  }
  return thunkApi.rejectWithValue(await response.json())
})

export const getSalesTeamUsers = createAsyncThunk<IUserPosition[], number>(
  'salesTeam/users',
  async (id, thunkApi) => {
    const response = await get(`${baseUrl}/${id}/users`)

    if (response.ok) return await response.json()
    return thunkApi.rejectWithValue(await response.json())
  },
)

export const updateDefaultSalesTeams = createAsyncThunk<
  IUpdateDefaultSalesTeamsInput,
  IUpdateDefaultSalesTeamsInput,
  { rejectValue: APIValidationError }
>('salesTeam/default/update', async (model, thunkApi) => {
  const response = await put(`${baseUrl}/updateDefault`, model)

  if (response.ok) return model
  return thunkApi.rejectWithValue(await response.json())
})

export const getSalesTeamsUsers = createAsyncThunk<
  AssigneeOption[],
  number[],
  { rejectValue: APIValidationError }
>('salesTeam/GetSalesTeamsUsers', async (ids, thunkApi) => {
  const response = await post(
    `${ServiceConfig.baseAPIUrl}/salesTeam/GetSalesTeamsUsers`,
    ids,
  )

  if (response.ok) {
    const result: AssigneeOption[] = await response.json()
    return result.map((owner) => ({
      id: owner.userId,
      userId: owner.userId,
      name: owner.name,
      salesTeamId: owner.salesTeamId,
      isActive: owner.isActive,
      isAdmin: owner.isAdmin,
    }))
  }

  return thunkApi.rejectWithValue(await response.json())
})

export const getSalesTeamsUsersByTeamStructure = createAsyncThunk<
  AssigneeOption[],
  number[],
  { rejectValue: APIValidationError }
>('salesTeam/GetUsersByTeamStructure', async (ids, thunkApi) => {
  const response = await post(
    `${ServiceConfig.baseAPIUrl}/salesTeam/getUsersByTeamStructure`,
    ids,
  )

  if (response.ok) {
    const result: AssigneeOption[] = await response.json()
    return result.map((owner) => ({
      id: owner.userId,
      userId: owner.userId,
      name: owner.name,
      salesTeamId: owner.salesTeamId,
      isActive: owner.isActive,
      isAdmin: owner.isAdmin,
    }))
  }

  return thunkApi.rejectWithValue(await response.json())
})
